import { NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AnalyticsConfig, AnalyticsDefaultParameters, AnalyticsSdk, DatadogConfig } from '@app/shared/interface/sdk-analytics';
import * as i0 from "@angular/core";
import * as i1 from "../../interface/sdk-analytics";
var SdkAnalyticsService = /** @class */ (function () {
    function SdkAnalyticsService(zone, cfg, platformId) {
        this.zone = zone;
        this.cfg = cfg;
        this.sdk = null;
        this.loadPromise = null;
        this.isBrowser = false;
        this.queue = [];
        this.isBrowser = isPlatformBrowser(platformId);
    }
    SdkAnalyticsService.prototype.loadSdk = function (gtmId, sdkUrlOverride, initializeParameters) {
        var _this = this;
        if (!this.isBrowser) {
            return Promise.resolve();
        }
        // Se já iniciamos o load, devolve a promise existente
        if (this.loadPromise !== null) {
            return this.loadPromise;
        }
        // Resolva os valores efetivos
        var url = sdkUrlOverride || (this.cfg ? this.cfg.sdkUrl : '');
        var gtm = gtmId || (this.cfg ? this.cfg.gtmId : '');
        var defaultParameters = initializeParameters || (this.cfg ? this.cfg.defaultParameters : undefined);
        // Persiste os valores efetivos
        this.currentSdkUrl = url;
        this.currentGtmId = gtm;
        this.currentDefaultParameters = defaultParameters;
        // Seta antes de carregar o script
        if (gtm) {
            window._containerGTM = gtm;
        }
        // Caminho rápido: factory global já existe ou script já foi injetado
        var factory = window.SDKAnalytics;
        var existing = document.querySelector('script[src="' + url + '"]');
        var onReady = function () {
            if (defaultParameters) {
                _this.initializeDefaultParameters(defaultParameters);
            }
            _this.flushQueue();
        };
        if (typeof factory === 'function' || existing) {
            this.loadPromise = this.waitUntilReady().then(onReady);
            return this.loadPromise;
        }
        // Inserir script dinamicamente fora da zona
        var self = this;
        this.loadPromise = new Promise(function (resolve, reject) {
            self.zone.runOutsideAngular(function () {
                var script = document.createElement('script');
                script.src = url;
                script.async = true;
                if (self.cfg && self.cfg.scriptNonce) {
                    script.nonce = String(self.cfg.scriptNonce);
                }
                script.onload = function () {
                    self.waitUntilReady().then(function () {
                        self.zone.run(function () {
                            onReady();
                            resolve();
                        });
                    }).catch(function (err) {
                        self.zone.run(function () { reject(err); });
                    });
                };
                script.onerror = function () {
                    self.zone.run(function () { reject(new Error('Erro ao carregar SDK Analytics: ' + url)); });
                };
                document.head.appendChild(script);
            });
        });
        return this.loadPromise;
    };
    Object.defineProperty(SdkAnalyticsService.prototype, "timeoutMs", {
        get: function () {
            return (this.cfg && typeof this.cfg.timeoutMs === 'number') ? this.cfg.timeoutMs : 10000;
        },
        enumerable: true,
        configurable: true
    });
    SdkAnalyticsService.prototype.isSdkReady = function () {
        if (!this.isBrowser) {
            return false;
        }
        var factory = window.SDKAnalytics;
        // Somente instancia quando tivermos um GTM efetivo.
        if (!this.sdk && typeof factory === 'function') {
            var gtmToUse = this.currentGtmId ||
                (typeof window._containerGTM === 'string' ? window._containerGTM : undefined);
            if (!gtmToUse) {
                // Ainda não sabemos o container correto -> não instancia
                return false;
            }
            try {
                this.sdk = factory({
                    gtm: gtmToUse,
                    webview: !!(this.cfg && this.cfg.webview),
                    engagement: this.cfg && this.cfg.engagement ? this.cfg.engagement : null,
                    fullstory: this.cfg && this.cfg.fullstory ? this.cfg.fullstory : null,
                    allowlist: true,
                    dual_tagging: true
                });
            }
            catch (e) {
                // noop
            }
        }
        var api = this.sdk;
        return !!api &&
            typeof api.track === 'function' &&
            typeof api.setUserProperties === 'function' &&
            typeof api.setDefaultParameters === 'function';
    };
    SdkAnalyticsService.prototype.waitUntilReady = function (timeout) {
        if (timeout === void 0) { timeout = this.timeoutMs; }
        if (!this.isBrowser)
            return Promise.resolve();
        if (this.isSdkReady())
            return Promise.resolve();
        var self = this;
        return new Promise(function (resolve, reject) {
            self.zone.runOutsideAngular(function () {
                var started = (window.performance && performance.now) ? performance.now() : Date.now();
                var timer = window.setInterval(function () {
                    if (self.isSdkReady()) {
                        window.clearInterval(timer);
                        self.zone.run(function () { resolve(); });
                    }
                    else {
                        var now = (window.performance && performance.now) ? performance.now() : Date.now();
                        if ((now - started) > timeout) {
                            window.clearInterval(timer);
                            self.zone.run(function () { reject(new Error('Timeout aguardando SDK pronto (métodos indisponíveis).')); });
                        }
                    }
                }, 50);
            });
        });
    };
    SdkAnalyticsService.prototype.initializeDefaultParameters = function (defaultParameter) {
        var _this = this;
        if (!defaultParameter) {
            return;
        }
        var call = function () {
            if (_this.sdk && _this.sdk.setDefaultParameters) {
                _this.sdk.setDefaultParameters({ analytics: { parameters: defaultParameter } });
            }
        };
        if (this.sdk)
            call();
        else
            this.queue.push(call);
    };
    SdkAnalyticsService.prototype.trackEvent = function (eventName, eventParams) {
        this.callSdk(function (sdk) {
            sdk.track({ analytics: { event_name: eventName, parameters: eventParams } });
        });
    };
    SdkAnalyticsService.prototype.setUserProperties = function (properties) {
        this.callSdk(function (sdk) {
            sdk.setUserProperties({ analytics: properties });
        });
    };
    SdkAnalyticsService.prototype.setDefaultParameters = function (parameters) {
        this.callSdk(function (sdk) {
            sdk.setDefaultParameters({ analytics: { parameters: parameters } });
        });
    };
    SdkAnalyticsService.prototype.removeUserProperties = function () {
        this.callSdk(function (sdk) {
            if (sdk.removeUserProperties) {
                sdk.removeUserProperties();
            }
        });
    };
    SdkAnalyticsService.prototype.removeDefaultParameter = function (parameter) {
        this.callSdk(function (sdk) {
            if (sdk.removeDefaultParameter) {
                sdk.removeDefaultParameter(parameter);
            }
        });
    };
    SdkAnalyticsService.prototype.removeDefaultParameters = function (parameters) {
        this.callSdk(function (sdk) {
            if (sdk.removeDefaultParameters) {
                sdk.removeDefaultParameters(parameters);
            }
        });
    };
    SdkAnalyticsService.prototype.whenReady = function () {
        if (!this.isBrowser)
            return Promise.resolve();
        if (this.loadPromise === null) {
            var cfg = this.cfg;
            var gtm = this.currentGtmId || (typeof window._containerGTM === 'string' ? window._containerGTM : (cfg ? cfg.gtmId : undefined));
            var url = this.currentSdkUrl || (cfg ? cfg.sdkUrl : undefined);
            var defaults = this.currentDefaultParameters || (cfg ? cfg.defaultParameters : undefined);
            this.loadPromise = this.loadSdk(gtm, url, defaults).catch(function () { });
        }
        return this.loadPromise;
    };
    SdkAnalyticsService.prototype.flushQueue = function () {
        var _this = this;
        if (!this.queue.length)
            return;
        if (!this.isSdkReady()) {
            this.waitUntilReady()
                .then(function () { return _this.flushQueue(); })
                .catch(function () { });
            return;
        }
        var tasks = this.queue.slice();
        this.queue.length = 0;
        this.zone.runOutsideAngular(function () {
            for (var i = 0; i < tasks.length; i++) {
                try {
                    tasks[i]();
                }
                catch (e) { /* swallow */ }
            }
        });
    };
    SdkAnalyticsService.prototype.callSdk = function (fn) {
        var _this = this;
        if (this.isSdkReady()) {
            this.zone.runOutsideAngular(function () {
                try {
                    fn(_this.sdk);
                }
                catch (e) { /* swallow */ }
            });
            return;
        }
        // Carregamento sob demanda (ainda privilegiando valores efetivos)
        if (this.loadPromise === null && this.isBrowser) {
            var cfg = this.cfg;
            var gtm = this.currentGtmId || (typeof window._containerGTM === 'string' ? window._containerGTM : (cfg ? cfg.gtmId : undefined));
            var url = this.currentSdkUrl || (cfg ? cfg.sdkUrl : undefined);
            var defaults = this.currentDefaultParameters || (cfg ? cfg.defaultParameters : undefined);
            this.loadSdk(gtm, url, defaults).catch(function () { });
        }
        if (this.queue.length >= 100) {
            this.queue.shift();
        }
        this.queue.push(function () {
            if (!_this.isSdkReady()) {
                _this.flushQueue();
                return;
            }
            try {
                fn(_this.sdk);
            }
            catch (e) { /* swallow */ }
        });
    };
    /**
     * Configura o Datadog via SDK Analytics.
     * @param config Configuração do Datadog
     */
    SdkAnalyticsService.prototype.configureDatadog = function (config) {
        this.callSdk(function (sdk) {
            sdk.track({
                analytics: {
                    event_name: 'datadog_init',
                    parameters: config
                }
            });
        });
    };
    /**
     * Envia log de erro para o Datadog via SDK.
     * @param message Mensagem de erro
     * @param context Contexto adicional
     */
    SdkAnalyticsService.prototype.sendDatadogErrorLog = function (message, context) {
        if (context === void 0) { context = {}; }
        this.callSdk(function (sdk) {
            sdk.track({
                analytics: {
                    event_name: 'log',
                    parameters: {
                        status: 'error',
                        message: message,
                        context: context,
                        error: null
                    }
                }
            });
        });
    };
    /**
     * Envia log informativo para o Datadog via SDK.
     * @param message Mensagem informativa
     * @param context Contexto adicional
     */
    SdkAnalyticsService.prototype.sendDatadogInfoLog = function (message, context) {
        if (context === void 0) { context = {}; }
        this.callSdk(function (sdk) {
            sdk.track({
                analytics: {
                    event_name: 'log',
                    parameters: {
                        status: 'info',
                        message: message,
                        context: context,
                        error: null
                    }
                }
            });
        });
    };
    /**
     * Inicia uma view no Datadog RUM via SDK.
     * @param pageName Nome da página
     * @param context Contexto adicional
     */
    SdkAnalyticsService.prototype.sendDatadogStartView = function (pageName, context) {
        if (context === void 0) { context = {}; }
        this.callSdk(function (sdk) {
            sdk.track({
                analytics: {
                    event_name: 'rum_start_view',
                    parameters: {
                        pageName: pageName,
                        context: context
                    }
                }
            });
        });
    };
    /**
     * Define o usuário no Datadog RUM via SDK.
     * @param id ID do usuário
     * @param name Nome do usuário
     */
    SdkAnalyticsService.prototype.sendDatadogSetUser = function (id, name) {
        this.callSdk(function (sdk) {
            sdk.track({
                analytics: {
                    event_name: 'rum_set_user',
                    parameters: {
                        id: id,
                        name: name
                    }
                }
            });
        });
    };
    SdkAnalyticsService.ngInjectableDef = i0.defineInjectable({ factory: function SdkAnalyticsService_Factory() { return new SdkAnalyticsService(i0.inject(i0.NgZone), i0.inject(i1.ANALYTICS_CONFIG, 8), i0.inject(i0.PLATFORM_ID)); }, token: SdkAnalyticsService, providedIn: "root" });
    return SdkAnalyticsService;
}());
export { SdkAnalyticsService };
